import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { colour, radius, shadow, space, text } from '../design/tokens.ts';
import { Button } from './basics.tsx';

/**
 * Screen 19's dialogs.
 *
 * One component, because every dialog in Maestro has the same shape: a title, a
 * sentence saying what is about to happen, the thing itself, and two buttons
 * where the right-hand one is the commitment. A big job says how big before the
 * button says yes.
 *
 * Escape closes it, focus moves into it, and the page behind does not scroll —
 * the small things that make a dialog usable without a mouse.
 */
export function Dialog({
  title,
  says,
  children,
  confirm,
  confirmWhyNot,
  onConfirm,
  onClose,
  busy,
  tone = 'primary'
}: {
  title: string;
  says?: ReactNode | undefined;
  children?: ReactNode | undefined;
  confirm: string;
  /** Why the confirm button is off, when it is. Said out loud, not a tooltip. */
  confirmWhyNot?: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean | undefined;
  tone?: 'primary' | 'danger' | undefined;
}) {
  const panel = useRef<HTMLDivElement>(null);

  // Moving focus into the dialog, and holding the page still behind it,
  // belong to the dialog *opening* — so this runs once and never again.
  //
  // It used to be one effect with `[onClose]`, and `onClose` is almost always
  // an inline arrow, which is a new function on every render. So every
  // keystroke in a dialog re-ran the effect and `panel.focus()` pulled the
  // caret out of whatever field was being typed in.
  useEffect(() => {
    const held = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.body.style.overflow = held;
    };
  }, []);

  // Escape does have to call the current `onClose`, so this one follows it —
  // adding and removing a key listener moves no focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(27, 28, 30, 0.44)',
        display: 'grid',
        placeItems: 'center',
        padding: 16
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(100%, 560px)',
          maxHeight: '86vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: space.control,
          padding: space.card,
          background: colour.surface,
          border: `1px solid ${colour.edge}`,
          borderRadius: radius.card,
          boxShadow: shadow.dialog,
          outline: 'none'
        }}
      >
        <h2 style={{ margin: 0, ...text.h2 }}>{title}</h2>
        {says && <span style={{ ...text.body, color: colour.inkSoft }}>{says}</span>}
        {children}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: space.tight,
            paddingTop: space.tight,
            borderTop: `1px solid ${colour.edgeSoft}`
          }}
        >
          <Button onClick={onClose} disabled={busy}>
            Not now
          </Button>
          <Button
            tone={tone}
            onClick={onConfirm}
            disabled={busy === true || confirmWhyNot != null}
            disabledReason={confirmWhyNot ?? ''}
          >
            {busy ? 'Working…' : confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
